import { useState, useRef, useEffect } from "react";
import { Tool } from "@/types/tool";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import mapboxgl from 'mapbox-gl';
import { FilterProvider } from './FilterProvider';
import MainContentSections from './MainContentSections';

interface MainContentProps {
  session: any;
  isEditing: boolean;
  categories: string[];
  address: string;
  latitude: number | null;
  longitude: number | null;
  businessName: string;
  businessPhone: string;
  businessWebsite: string;
  businessDescription: string;
  businessImageUrl?: string | null;
  onCategoriesChange: (value: string[]) => void;
  onEditToggle: () => void;
  onSaveProfile: () => void;
  onLocationSelect: (location: { 
    lat: number; 
    lng: number; 
    formattedAddress: string;
    placeId?: string;
    businessDetails?: {
      name?: string;
      phoneNumber?: string;
      website?: string;
      rating?: number;
      userRatingsTotal?: number;
      reviews?: google.maps.places.PlaceReview[];
      photoUrl?: string;
    };
  }) => void;
  onBusinessNameChange: (value: string) => void;
  onBusinessPhoneChange: (value: string) => void;
  onBusinessWebsiteChange: (value: string) => void;
  onBusinessDescriptionChange: (value: string) => void;
}

const MainContent = ({
  session,
  isEditing,
  categories,
  address,
  latitude,
  longitude,
  businessName,
  businessPhone,
  businessWebsite,
  businessDescription,
  onCategoriesChange,
  onEditToggle,
  onSaveProfile,
  onLocationSelect,
  onBusinessNameChange,
  onBusinessPhoneChange,
  onBusinessWebsiteChange,
  onBusinessDescriptionChange,
}: MainContentProps) => {
  const [isMapExpanded, setIsMapExpanded] = useState(false);
  const [tools, setTools] = useState<Tool[]>([]);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    fetchTools();

    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const fetchTools = async () => {
    try {
      const { data, error } = await supabase
        .from('professional_profiles')
        .select('*')
        .order('updated_at', { ascending: false });

      if (error) throw error;

      const uniqueBusinesses = new Map();

      const businessesWithReviews = await Promise.all(
        data.map(async (profile) => {
          if (profile.place_id) {
            try {
              const { data: placeDetails } = await supabase.functions.invoke('get-place-details', {
                body: { placeId: profile.place_id }
              });

              if (placeDetails?.success) {
                let categories = profile.categories || [];
                if (placeDetails.data.reviews) {
                  const reviewText = placeDetails.data.reviews
                    .map((review: any) => review.text)
                    .join(' ')
                    .toLowerCase();
                  
                  if (reviewText.includes('install') || reviewText.includes('installation')) {
                    categories = [...new Set([...categories, 'installer'])];
                  }
                  if (reviewText.includes('product') || reviewText.includes('purchase') || reviewText.includes('buy')) {
                    categories = [...new Set([...categories, 'retailer'])];
                  }
                  if (reviewText.includes('manufacture') || reviewText.includes('production')) {
                    categories = [...new Set([...categories, 'manufacturer'])];
                  }

                  await supabase
                    .from('professional_profiles')
                    .update({ categories })
                    .eq('id', profile.id);
                }

                return {
                  ...profile,
                  categories,
                  business_rating: placeDetails.data.rating || profile.business_rating,
                  business_total_ratings: placeDetails.data.total_ratings || profile.business_total_ratings,
                  business_reviews: placeDetails.data.reviews || profile.business_reviews,
                };
              }
            } catch (error) {
              console.error('Error fetching place details:', error);
            }
          }
          return profile;
        })
      );

      businessesWithReviews.forEach(profile => {
        if (profile) {
          uniqueBusinesses.set(profile.id, {
            id: profile.id,
            name: profile.business_name,
            description: profile.bio || '',
            logo: profile.business_image_url || '/tools/installer.png',
            categories: profile.categories || [],
            tags: profile.categories || [],
            rating: profile.business_rating || 0,
            reviews: profile.business_total_ratings || 0,
            bookmarks: 0,
            featured: true,
            pricing: 'Quote-based',
            visitUrl: profile.business_website || '#',
            latitude: profile.lat,
            longitude: profile.lng,
            business_phone: profile.business_phone
          });
        }
      });

      const mappedTools: Tool[] = Array.from(uniqueBusinesses.values());
      setTools(mappedTools);
    } catch (error) {
      console.error('Error fetching tools:', error);
      toast.error('Failed to load business profiles');
    }
  };

  return (
    <FilterProvider>
      <MainContentSections
        session={session}
        isEditing={isEditing}
        tools={tools}
        mapRef={mapRef}
        isMapExpanded={isMapExpanded}
        setIsMapExpanded={setIsMapExpanded}
        latitude={latitude}
        longitude={longitude}
        onLocationSelect={onLocationSelect}
        categories={categories}
        address={address}
        businessName={businessName}
        businessPhone={businessPhone}
        businessWebsite={businessWebsite}
        businessDescription={businessDescription}
        onCategoriesChange={onCategoriesChange}
        onEditToggle={onEditToggle}
        onSaveProfile={onSaveProfile}
        onBusinessNameChange={onBusinessNameChange}
        onBusinessPhoneChange={onBusinessPhoneChange}
        onBusinessWebsiteChange={onBusinessWebsiteChange}
        onBusinessDescriptionChange={onBusinessDescriptionChange}
        isMobile={isMobile}
      />
    </FilterProvider>
  );
};

export default MainContent;
